import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withKeys as _withKeys, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-d315fd04"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "grid gap-y-8 md:grid-cols-2 md:gap-x-8" }
const _hoisted_2 = { class: "flex items-center gap-3" }
const _hoisted_3 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-x-8" }
const _hoisted_4 = { class: "border border-capGrey overflow-auto max-h-80 mt-3" }
const _hoisted_5 = {
  key: 0,
  class: "mt-8"
}
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-capDarkPurple text-xl font-semibold" }, "Rental Detail", -1))
const _hoisted_7 = { class: "grid grid-cols-2 gap-6 mt-3" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-capGrey text-sm" }, "Property Manager", -1))
const _hoisted_9 = { class: "text-capDarkPurple" }
const _hoisted_10 = { class: "text-capDarkPurple" }
const _hoisted_11 = { class: "text-capDarkPurple" }
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-capGrey text-sm" }, "Tenant", -1))
const _hoisted_13 = { class: "text-capDarkPurple" }
const _hoisted_14 = { class: "text-capDarkPurple" }
const _hoisted_15 = { class: "text-capDarkPurple" }
const _hoisted_16 = { class: "h-full flex flex-col" }
const _hoisted_17 = { class: "text-center font-light text-capGrey" }
const _hoisted_18 = ["onKeydown"]
const _hoisted_19 = ["onKeydown"]
const _hoisted_20 = ["srcdoc"]

import { useCurrency } from '@/composables/useCurrency'
    import { computed, ref, onMounted, onUnmounted } from 'vue'
    import { splitAddressLabel } from '@/composables/useAddressLabel'
    import { useStore } from 'vuex'
    import axios from 'axios'
    import agreementLogo from '@/assets/agreement-logo.png'
    import agreementBbbSeal from '@/assets/agreement-bbb-seal.png'

    
export default /*#__PURE__*/_defineComponent({
  __name: 'ContractInfo',
  props: {id: String},
  setup(__props) {

const props = __props

    

    // Letter page at 96dpi. The preview iframe always renders at this true
    // size so text wraps exactly as it will on the real page; the box scales
    // the result down to fit visually (see previewScale/updatePreviewScale).
    const PAGE_WIDTH_PX = 816

    const agreementBox = ref<HTMLElement | null>(null)
    const previewScale = ref(1)
    let resizeObserver: ResizeObserver | null = null

    function updatePreviewScale() {
        if (!agreementBox.value) return
        previewScale.value = agreementBox.value.clientWidth / PAGE_WIDTH_PX
    }

    onMounted(() => {
        if (agreementBox.value) {
            updatePreviewScale()
            resizeObserver = new ResizeObserver(updatePreviewScale)
            resizeObserver.observe(agreementBox.value)
        }
    })

    onUnmounted(() => {
        resizeObserver?.disconnect()
    })

    // agreementHTML embeds these two brand images by external URL (a
    // WordPress-hosted logo and a Gmail-proxy-wrapped BBB seal link). Both are
    // static across every contract, so they're bundled locally and swapped in
    // here rather than relying on those external links staying up.
    function replaceStaticImages(html: string): string {
        return html
            .replace(/src=(['"])[^'"]*logo-2\.png[^'"]*\1/i, `src='${agreementLogo}'`)
            .replace(/src=(['"])[^'"]*bbb2\.png[^'"]*\1/i, `src='${agreementBbbSeal}'`)
    }

    // The calc's very first table has no width attribute (every later table
    // explicitly sets width='100%'), so it shrinks to fit its content instead
    // of spanning the page: the address/agreement-number/logo row bunches on
    // the left with a blank gap on the right. Forcing all tables to full
    // width fixes that, and body margin gives the page some breathing room.
    // Kept in sync with the same fix in downloadDocument.php.
    function fixLayout(html: string): string {
        // The company-info/BBB-seal footer is the only table with height='100'
        // in this document, letting it be targeted precisely, and pinning it to
        // the bottom of the page (rather than floating right after the last
        // section) means it lands wherever the content happens to end
        // otherwise, which varies a lot by contract length. Wrapping the table
        // in a plain div and positioning THAT (leaving the table itself in
        // normal flow) rather than positioning the table directly: Dompdf
        // truncates this table's multi-line content when position is applied
        // to the <table> itself (a Dompdf-specific quirk, not a browser one,
        // but kept identical here anyway). body needs an explicit min-height
        // (one letter page, minus its own margins) so "bottom:0" on that div
        // lands at the bottom of the page rather than wherever the body's
        // natural (shorter) content height ends.
        const wrapped = html.replace(
            /<table([^>]*\bheight=(['"])100\2[^>]*)>([\s\S]*?)<\/table>/i,
            `<div style='position:absolute;bottom:0;left:0;right:0'><table$1>$3</table></div>`
        )
        return wrapped.replace(
            '<style></style>',
            '<style>table{width:100%}body{position:relative;min-height:9.85in;margin:0.7in 0.45in 0.45in 0.45in}td+td{padding-left:14px}</style>'
        )
    }

    // Every explicit font-size in the calc's HTML is small (9-12px). Bumping
    // each up a couple pixels keeps their relative hierarchy (the disclaimer
    // text stays smaller than the main content) while making everything a
    // bit more readable. Kept in sync with the same fix in downloadDocument.php.
    function bumpFontSizes(html: string): string {
        return html.replace(/font-size:\s*(\d+(?:\.\d+)?)px/gi, (_match, size) => {
            return `font-size:${(parseFloat(size) + 2).toFixed(1).replace(/\.0$/, '')}px`
        })
    }

    const store = useStore()
    const user = computed(() => store.state.user)
    const contract = computed(() => store.state.contract)
    const addressLabel = computed(() => splitAddressLabel(contract.value.zctWebPhysicalAddressLabel))
    const agreementHTML = computed(() => {
        const raw = store.state.contract.agreementHTML
        if (!raw) return ''
        const stripped = raw.startsWith('data:text/html,')
            ? raw.slice('data:text/html,'.length)
            : raw
        try {
            return bumpFontSizes(fixLayout(replaceStaticImages(decodeURIComponent(stripped))))
        } catch {
            return bumpFontSizes(fixLayout(replaceStaticImages(stripped)))
        }
    })
    const productList = computed(() => {
        const raw = contract.value.zctAddOnArray
        const addOns: Record<string, Record<string, unknown>> =
            typeof raw === 'string' ? (raw ? JSON.parse(raw) : {}) : (raw ?? {})
        return Object.values(addOns).flatMap((entry) =>
            Object.entries(entry).map(([label, value]) => ({ label, value }))
        )
    })

    const defaultCurrency = computed(
        () => user.value?.currencyCode ?? (user.value?.country === 'CA' ? 'CAD' : 'USD')
    )
    const defaultLocale = computed(
        () => user.value?.locale ?? (defaultCurrency.value === 'CAD' ? 'en-CA' : 'en-US')
    )

    const { formatMoney } = useCurrency({
        defaultCurrency: defaultCurrency.value,
        locale: defaultLocale.value
    })

    function displayValue(val: unknown) {
        const n = Number(val)
        return Number.isFinite(n) ? formatMoney(n) : String(val ?? '')
    }

    function downloadAgreementPdf() {
        if (!props.id) return

        // A direct navigation, not a fetch: the endpoint returns real PDF bytes
        // with a Content-Disposition filename header, which only works if the
        // browser requests the URL itself (that's also why this doesn't need
        // the "open a blank tab first" popup-blocker workaround the old
        // blob-based version needed, this is a normal synchronous navigation).
        const url = `${axios.defaults.baseURL}downloadDocument.php?documentID=${encodeURIComponent(props.id)}`
        window.open(url, '_blank')
    }

    function onAgreementClick(e: MouseEvent) {
        const target = e.target as Element
        if (target.closest('button, input, textarea, select, [contenteditable="true"]')) {
            return
        }
        const link = target.closest('a')
        if (link) {
            e.preventDefault()
        }
        downloadAgreementPdf()
    }

return (_ctx: any,_cache: any) => {
  const _component_c_container_heading = _resolveComponent("c-container-heading")!
  const _component_c_badge = _resolveComponent("c-badge")!
  const _component_c_container_text = _resolveComponent("c-container-text")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _createElementVNode("div", null, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_c_container_heading, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(contract).contractNumberDisplay), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_c_badge, {
            status: _unref(contract).status
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(contract).status), 1)
            ]),
            _: 1
          }, 8, ["status"])
        ]),
        _createVNode(_component_c_container_text, null, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(addressLabel).street), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_c_container_text, null, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(addressLabel).cityStateZip), 1)
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_c_container_text, {
            class: "mt-3",
            secondary: ""
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString('Active on ' + _unref(contract).activeDate), 1)
            ]),
            _: 1
          }),
          (_unref(contract).cancellationDate)
            ? (_openBlock(), _createBlock(_component_c_container_text, {
                key: 0,
                class: "mt-3",
                secondary: ""
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString('Cancelled on ' + _unref(contract).cancellationDate), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(productList), (item, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: item.label
          }, [
            _createElementVNode("div", {
              class: _normalizeClass([[index % 2 === 0 ? 'bg-capLightPurple bg-opacity-25' : '', item.label === 'Total' ? 'font-semibold' : ''], "text-capDarkPurple flex justify-between text-base px-4 py-2"])
            }, [
              _createElementVNode("span", null, _toDisplayString(item.label), 1),
              _createElementVNode("span", null, _toDisplayString(displayValue(item.value)), 1)
            ], 2)
          ]))
        }), 128))
      ]),
      (_unref(contract).rental == 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _hoisted_6,
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", null, [
                _hoisted_8,
                _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(contract).zctPropertyManagerName || '—'), 1),
                _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(contract).zctPropertyManagerEmail || '—'), 1),
                _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(contract).zctPropertyManagerPhone || '—'), 1)
              ]),
              _createElementVNode("div", null, [
                _hoisted_12,
                _createElementVNode("p", _hoisted_13, _toDisplayString(_unref(contract).zctTenantName || '—'), 1),
                _createElementVNode("p", _hoisted_14, _toDisplayString(_unref(contract).zctTenantEmail || '—'), 1),
                _createElementVNode("p", _hoisted_15, _toDisplayString(_unref(contract).zctTenantPhone || '—'), 1)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_16, [
      _createElementVNode("p", _hoisted_17, [
        _createElementVNode("span", {
          class: "underline cursor-pointer",
          role: "button",
          tabindex: "0",
          onClick: downloadAgreementPdf,
          onKeydown: [
            _withKeys(downloadAgreementPdf, ["enter"]),
            _withKeys(_withModifiers(downloadAgreementPdf, ["prevent"]), ["space"])
          ]
        }, " Click PDF preview to view full agreement ", 40, _hoisted_18)
      ]),
      _createElementVNode("div", {
        ref_key: "agreementBox",
        ref: agreementBox,
        class: "agreement-print m-4 border border-capGrey bg-white",
        onClick: onAgreementClick,
        role: "button",
        tabindex: "0",
        onKeydown: [
          _withKeys(_withModifiers(downloadAgreementPdf, ["stop","prevent"]), ["enter"]),
          _withKeys(_withModifiers(downloadAgreementPdf, ["stop","prevent"]), ["space"])
        ]
      }, [
        _createElementVNode("iframe", {
          srcdoc: _unref(agreementHTML),
          style: _normalizeStyle({ transform: `scale(${previewScale.value})` }),
          class: "agreement-frame border-0 pointer-events-none",
          tabindex: "-1",
          title: "Agreement preview"
        }, null, 12, _hoisted_20)
      ], 40, _hoisted_19)
    ])
  ]))
}
}

})